import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { serialize } from '../../utils/helpers/model.helper';
import { Client } from '../client-data/client.model';
import { IMessageDeliveryData } from '../message-delivery/message-delivery.interface';
import { CompanyInfo } from '../settings-data/sub-models/company-info.model';
import { PaymentsSettings } from '../settings-data/sub-models/company-settings/payments.model';
import { DocumentPresetSettings } from '../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { MoneyPipe } from '../shared/pipes/money/money.pipe';
import { EmailService } from '../shared/services/email/email.service';
import { StatementContent } from './models/statement-content.model';
import { StatementInfo } from './models/statement-info.model';
import { Statement } from './models/statement.model';
import { IStatementData, IStatementInfoParams } from './statement-data.data';
import { v4 } from 'uuid';
import { environment } from '../../../environments/environment';

@Injectable()
export class StatementDataBuilder {
  model = Statement;

  constructor(
    private _emailService: EmailService,
    private _moneyPipe: MoneyPipe,
    private _translateService: TranslateService
  ) {}

  buildStatementInfo(
    statementInfo: StatementInfo,
    settings: DocumentPresetSettings,
    companyInfo: CompanyInfo,
    client: Client
  ): StatementInfo {
    return statementInfo.withMutations((map) => {
      const statementKey = StatementInfo.KEYS.STATEMENT;

      if (settings) {
        map.setIn([statementKey, Statement.KEYS.SETTINGS], settings);
      }

      if (companyInfo) {
        map.setIn([statementKey, Statement.KEYS.COMPANY_INFO], companyInfo);
      }

      if (client) {
        map.setIn([statementKey, Statement.KEYS.CLIENT_CONTENT], client);
      }

      map.setIn(
        [statementKey, Statement.KEYS.LINKS],
        map.get(StatementInfo.KEYS.LINKS)
      );
      map.setIn([statementKey, Statement.KEYS.VIEW_ID], v4());
    }) as StatementInfo;
  }

  buildStatementsPayload(
    clientId: string,
    clientName: string,
    startDate: string,
    endDate: string,
    unpaidOnly: boolean,
    currencyCode: string
  ): IStatementInfoParams {
    return {
      client_billing_name: clientName,
      client_id: clientId,
      end_date: endDate,
      start_date: startDate,
      unpaid_only: unpaidOnly,
      currency_code: currencyCode,
    };
  }

  buildSendStatementPayload(
    statementInfo: StatementInfo,
    accountEmail: string,
    paymentsSettings: PaymentsSettings,
    cardPaymentsFeatureEnabled: boolean,
    paypalFeatureEnabled: boolean,
    messageData: IMessageDeliveryData
  ): IStatementData {
    const emailMessage: string = messageData.message.replace(/[\n]/g, '<br/>');
    const subject: string = messageData.subject;
    const statement: Statement = statementInfo.statement;
    const statementContent: StatementContent = statement.content;
    const cardPaymentsEnabled: boolean =
      paymentsSettings.cardPaymentsEnabled() &&
      statementContent.cardPaymentsEnabled;
    const paypalEcEnabled: boolean =
      paymentsSettings.paypalEcEnabled && statementContent.paypalEcEnabled;
    const cardPaymentsGloballyEnabled: boolean =
      cardPaymentsFeatureEnabled && cardPaymentsEnabled;
    const paypalEcGloballyEnabled: boolean =
      paypalFeatureEnabled && paypalEcEnabled;

    return {
      statement: serialize(statement),
      delivery_options: {
        template_id: environment.statementEmailTemplateId,
        template_data: {
          language: statementContent.locale.split('-')[0],
          statement_url: statementInfo.links.statementUrl,
          card_payments_globally_enabled: cardPaymentsGloballyEnabled,
          paypal_ec_globally_enabled: paypalEcGloballyEnabled,
          subject_link_spamcheck: subject,
          body_spamcheck: emailMessage,
          super_subject: this._translateService.instant(
            'STATEMENTS.EMAIL.SUPER_SUBJECT',
            {
              amount: this._moneyPipe.transform(
                statement.amountDue,
                statementContent.currencyCode,
                statementContent.locale
              ),
            }
          ),
          show_footer: messageData.includeFooter,

          // will result in an object where the key is the card type and the value is true
          payment_methods: paymentsSettings.availableCardTypes.reduce(
            (result: any, cardType: string) => {
              result[cardType] = true;

              return result;
            },
            {}
          ),
        },
        recipients: this._emailService.tokenizeEmails(messageData.to),
        cc_emails: this._emailService.tokenizeEmails(messageData.cc),
        bcc_emails: this._emailService.tokenizeEmails(messageData.bcc),
        subject,
        from_name: statement.companyInfo.name || accountEmail,
        reply_to_email: accountEmail,
        file_name: messageData.fileName,
      },
    };
  }
}
